/**
 * UnityBridge.js
 *
 * Handles bidirectional communication between React and Unity WebGL / HTML
 * games using postMessage.
 */

import {
  BRIDGE_CHANNEL,
  REACT_SOURCE,
  normalizeBridgeMessage
} from './GameBridgeProtocol';

export class UnityBridge {
  constructor({ autoListen = true } = {}) {
    this.unityIframe = null;
    this.unityWindow = null;
    this.messageHandlers = new Map();
    this.isReady = false;
    this.messageQueue = [];
    this.responseCallbacks = new Map();
    this.messageId = 0;
    this.boundHandleMessage = (event) => this.handleMessage(event);

    if (autoListen && typeof window !== 'undefined') {
      window.addEventListener('message', this.boundHandleMessage);
    }
  }

  /**
   * Set Unity iframe reference
   * @param {HTMLIFrameElement} iframe - Unity iframe element
   */
  setIframe(iframe) {
    this.unityIframe = iframe;
    this.unityWindow = iframe ? iframe.contentWindow : null;
    console.log('[UnityBridge] Iframe set');
  }

  setUnityWindow(targetWindow) {
    this.unityWindow = targetWindow;
    console.log('[UnityBridge] Unity window set');
  }

  /**
   * Handle incoming messages from Unity or standard game modules.
   */
  handleMessage(event) {
    if (event.origin && event.origin !== window.location.origin) return;

    const message = normalizeBridgeMessage(event.data);
    if (!message) return;

    if (event.source && typeof event.source.postMessage === 'function') {
      this.unityWindow = event.source;
    }

    console.log('[UnityBridge] Received from game:', message.type, message.payload);

    if (message.type === 'game_ready') {
      this.isReady = true;
      this.flushMessageQueue();
      this.notifyHandlers('game_ready', message.payload, message);
      if (message.legacyType === 'unity_ready') {
        this.notifyHandlers('unity_ready', message.payload, message);
      }
      return;
    }

    if (message.messageId && this.responseCallbacks.has(message.messageId)) {
      const callback = this.responseCallbacks.get(message.messageId);
      callback(message.payload);
      this.responseCallbacks.delete(message.messageId);
      return;
    }

    this.notifyHandlers(message.type, message.payload, message);
  }

  /**
   * Notify all handlers for a message type
   */
  notifyHandlers(type, payload, message = {}) {
    if (this.messageHandlers.has(type)) {
      const handlers = this.messageHandlers.get(type);
      handlers.forEach(handler => handler(payload, message));
    }
  }

  /**
   * Register a message handler
   * @param {string} type - Message type to listen for
   * @param {Function} handler - Handler function
   */
  on(type, handler) {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }
    this.messageHandlers.get(type).push(handler);
  }

  /**
   * Unregister a message handler
   */
  off(type, handler) {
    if (this.messageHandlers.has(type)) {
      const handlers = this.messageHandlers.get(type);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Send message to Unity
   * @param {string} type - Message type
   * @param {Object} payload - Message data
   * @param {Function} callback - Optional callback for response
   */
  sendToGame(type, payload = {}, options = {}) {
    const messageId = this.messageId++;
    const message = {
      channel: BRIDGE_CHANNEL,
      source: REACT_SOURCE,
      type,
      payload,
      messageId: options.messageId ?? messageId
    };

    this.dispatchMessage(message, options.callback || null);
  }

  sendToUnity(type, payload = {}, callback = null) {
    const messageId = this.messageId++;
    const message = {
      source: 'react',
      type,
      payload,
      messageId
    };

    this.dispatchMessage(message, callback);
  }

  dispatchMessage(message, callback = null) {
    if (callback) {
      this.responseCallbacks.set(message.messageId, callback);
    }

    const target = this.unityWindow || (this.unityIframe ? this.unityIframe.contentWindow : window);

    if (!this.isReady || !target) {
      this.messageQueue.push(message);
      console.log('[UnityBridge] Message queued (Unity not ready):', message.type);
      return;
    }

    try {
      target.postMessage(message, '*');
      console.log('[UnityBridge] Sent to game:', message.type, message.payload);
    } catch (error) {
      console.error('[UnityBridge] Failed to send message:', error);
    }
  }

  /**
   * Flush queued messages when Unity becomes ready
   */
  flushMessageQueue() {
    console.log('[UnityBridge] Flushing message queue:', this.messageQueue.length);
    
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      const target = this.unityWindow || (this.unityIframe ? this.unityIframe.contentWindow : window);
      if (!target) return;
      target.postMessage(message, '*');
    }
  }

  /**
   * Game event handlers
   */
  
  // Notify Unity when player starts game
  startGame(config) {
    this.sendToUnity('start_game', config);
  }

  // Notify Unity when player pauses
  pauseGame() {
    this.sendToUnity('pause_game');
  }

  // Notify Unity when player resumes
  resumeGame() {
    this.sendToUnity('resume_game');
  }

  // Notify Unity when player quits
  quitGame() {
    this.sendToUnity('quit_game');
  }

  // Send configuration to Unity
  sendConfig(config) {
    this.sendToUnity('config', config);
  }

  // Request game state from Unity
  requestGameState(callback) {
    this.sendToUnity('get_state', {}, callback);
  }

  /**
   * Telemetry integration
   */
  
  // Notify Unity that telemetry started
  telemetryStarted(sessionId) {
    this.sendToUnity('telemetry_started', { sessionId });
  }

  // Notify Unity that telemetry stopped
  telemetryStopped() {
    this.sendToUnity('telemetry_stopped');
  }

  /**
   * Clean up
   */
  destroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('message', this.boundHandleMessage);
    }
    this.messageHandlers.clear();
    this.responseCallbacks.clear();
    this.messageQueue = [];
    this.unityIframe = null;
    this.unityWindow = null;
    this.isReady = false;
  }
}

// Singleton instance
const unityBridge = new UnityBridge();

export default unityBridge;
